import { concat, flatten, map } from "ramda";

import { FileDefinition } from "./getExtensionInfo.ts";
import { getFilenameInfo } from "./getFilenameInfo.ts";

export type FD = keyof typeof FileDefinition;

export const isFilenameOfType =
  (type: string | string[]) =>
  (path?: string | null): boolean => {
    if (!type) {
      return true;
    }
    // We are not going to use `getFilenameInfo` here because of the lookbehind support.
    // So instead of testing only the extracted extension, we simply test if
    // the extension pattern exactly exists in the filename, between 2 dots or
    // after a dot and the end of the string.
    // @see FileDefinition ext regular expression
    const fileInfo = getFilenameInfo(path);

    if (fileInfo?.extension && fileInfo.filename) {
      // Need to add a dot in order to test against the FileDefinition.
      const name = fileInfo.isWeak ? fileInfo.filename : `.${fileInfo.extension}`;

      if (Array.isArray(type)) {
        return type.some((t) => isMatchingExtension(t?.toUpperCase(), name));
      }

      return isMatchingExtension(type?.toUpperCase(), name);
    }

    return false;
  };

export function isFileDefinitionType(type: FD | string): type is FD {
  return !!FileDefinition[type as FD];
}
const isMatchingExtension = (type: FD | string, name: string) => {
  if (isFileDefinitionType(type)) {
    return !!name.match(FileDefinition[type].ext);
  }

  return name.toUpperCase().endsWith(type);
};
export const extractExtensionsFromType: (type: string) => string[] | null = (type) => {
  const fileType = type.toUpperCase();
  if (isFileDefinitionType(fileType)) {
    const def = FileDefinition[fileType];

    if (def) {
      const regex = def.ext;
      const match = regex.source.match(/\(([^)]+)\)/);
      const archiveExtensions = FileDefinition.ARCHIVE.ext.source.match(/\(([^)]+)\)/)?.[1].split("|");

      if (match) {
        const extensions = match[1].split("|");
        const archives = flatten(map((ext) => map((aext) => `${ext}.${aext}`, archiveExtensions!), extensions));

        return concat(extensions, archives);
      }
    }
  }

  return [fileType];
};
